import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';

export type ConfirmTone = 'default' | 'danger' | 'warning';

export interface ConfirmOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * Require the user to type an exact phrase (typically the resource name)
   * before the confirm button enables. Use for irreversible destructive
   * actions (delete customer, remove workspace, etc.). The phrase is
   * compared case-sensitively after trimming.
   */
  requireTyped?: string;
  /** Label for the typed-confirm input. Defaults to: `Type "<phrase>" to confirm`. */
  requireTypedLabel?: React.ReactNode;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/**
 * Imperative confirm() replacement. Usage:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete?', tone: 'danger' })) { ... }
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx.confirm;
}

interface PendingState extends ConfirmOptions {
  open: boolean;
  resolver: ((value: boolean) => void) | null;
}

const INITIAL: PendingState = {
  open: false,
  title: '',
  resolver: null,
};

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PendingState>(INITIAL);
  const [typed, setTyped] = useState<string>('');
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setTyped('');
      setState({ ...opts, open: true, resolver: resolve });
    });
  }, []);

  const close = useCallback(
    (result: boolean) => {
      state.resolver?.(result);
      setState(INITIAL);
      setTyped('');
    },
    [state]
  );

  const typedPhrase = state.requireTyped?.trim() ?? '';
  const typedRequired = typedPhrase.length > 0;
  const typedMatches = !typedRequired || typed.trim() === typedPhrase;

  const tone = state.tone ?? 'default';
  const confirmVariant = tone === 'danger' ? 'danger' : 'primary';
  const Icon = tone === 'danger' || tone === 'warning' ? AlertTriangle : HelpCircle;
  const iconClass =
    tone === 'danger'
      ? 'text-danger-600 bg-danger-50 dark:text-danger-300 dark:bg-danger-900/30'
      : tone === 'warning'
        ? 'text-warning-600 bg-warning-50 dark:text-warning-300 dark:bg-warning-900/30'
        : 'text-brand-600 bg-brand-50 dark:text-brand-300 dark:bg-brand-900/30';

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => close(false)}
        size="sm"
        initialFocus={confirmRef}
        footer={
          <>
            <Button variant="secondary" onClick={() => close(false)}>
              {state.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              ref={confirmRef}
              variant={confirmVariant}
              onClick={() => close(true)}
              disabled={!typedMatches}
            >
              {state.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-4">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconClass}`}
            aria-hidden="true"
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-h3 text-neutral-900 dark:text-neutral-100">
              {state.title}
            </div>
            {state.description && (
              <div className="mt-1.5 text-body-sm text-neutral-600 dark:text-neutral-400">
                {state.description}
              </div>
            )}
            {typedRequired && (
              <div className="mt-4">
                <label
                  htmlFor="confirm-typed-input"
                  className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
                >
                  {state.requireTypedLabel ?? (
                    <>
                      Type{' '}
                      <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.85em] text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200">
                        {typedPhrase}
                      </code>{' '}
                      to confirm
                    </>
                  )}
                </label>
                <input
                  id="confirm-typed-input"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && typedMatches) close(true);
                  }}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-mono text-neutral-900 placeholder:text-neutral-400 focus:border-danger-500 focus:outline-none focus:ring-2 focus:ring-danger-500/30 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                  placeholder={typedPhrase}
                  aria-invalid={!typedMatches}
                />
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
